import { BrowserRouter as Router, Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';
import './theme-light.css';
import Dashboard from './pages/Dashboard';
import Users from './pages/users/users';
import UserView from './pages/users/View/UserView';
import Organizations from './pages/Organizations';
import OrganizationProfileDetail from './pages/OrganizationProfileDetail';
import Systems from './pages/Systems';
import SmsGateway from './pages/SmsGateway';
import SystemRegistry from './pages/SystemRegistry';
import SupportInbox from './pages/SupportInbox';
import SupportCaseReview from './pages/SupportCaseReview';
import SafetyDesk from './pages/SafetyDesk';
import ContentControl from './pages/ContentControl';
import MonetizationReadiness from './pages/MonetizationReadiness';
import PlatformHealth from './pages/PlatformHealth';
import PrivacyOps from './pages/PrivacyOps';
import SecurityOperations from './pages/SecurityOperations';
import GrowthPulse from './pages/GrowthPulse';
import CallAnalytics from './pages/CallAnalytics';
import CommandAlerts from './pages/CommandAlerts';
import Login from './pages/Login';
import Register from './pages/Register';
import Landing from './pages/Landing';
import Download from './pages/Download';
import VerificationDesk from './pages/VerificationDesk';
import AdminClients from './pages/oauth/AdminClients';
import StartLink from './pages/oauth/StartLink';
import ProductDesk from './pages/ProductDesk';
import Notifications from './pages/Notifications';

function ProtectedRoute({ children }) {
  const token = localStorage.getItem('token');
  return token ? children : <Navigate to="/login" replace />;
}

function LegacyRedirect({ to }) {
  const location = useLocation();
  return <Navigate to={`${to}${location.search}${location.hash}`} replace />;
}

function LegacyDeskRedirect() {
  const { productKey } = useParams();
  const location = useLocation();
  return <Navigate to={`/desk/${productKey}${location.search}${location.hash}`} replace />;
}

function SessionAwareFallback() {
  const token = localStorage.getItem('token');
  return <Navigate to={token ? '/dashboard' : '/'} replace />;
}

const legacyRouteTargets = [
  ['/admin', '/dashboard'],
  ['/overview', '/dashboard'],
  ['/growth-pulse', '/growth'],
  ['/verification-desk', '/verification'],
  ['/verification-queue', '/verification'],
  ['/business-manager', '/organizations'],
  ['/business-profiles', '/organizations'],
  ['/systems/registry', '/systems/new'],
  ['/support-inbox', '/support'],
  ['/support-cases', '/support'],
  ['/safety-desk', '/safety'],
  ['/social-reports', '/safety'],
  ['/security-operations', '/security-ops'],
  ['/privacy-requests', '/privacy-ops'],
  ['/monetization-readiness', '/monetization'],
  ['/broadcasts', '/content'],
  ['/content-control', '/content'],
  ['/oauth/admin-clients', '/oauth/clients'],
];

export default function App() {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/download" element={<Download />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />

        <Route
          path="/oauth/clients"
          element={
            <ProtectedRoute>
              <AdminClients />
            </ProtectedRoute>
          }
        />
        <Route
          path="/oauth/connect"
          element={
            <ProtectedRoute>
              <StartLink />
            </ProtectedRoute>
          }
        />

        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/growth"
          element={
            <ProtectedRoute>
              <GrowthPulse />
            </ProtectedRoute>
          }
        />
        <Route
          path="/call-analytics"
          element={
            <ProtectedRoute>
              <CallAnalytics />
            </ProtectedRoute>
          }
        />
        <Route
          path="/command-alerts"
          element={
            <ProtectedRoute>
              <CommandAlerts />
            </ProtectedRoute>
          }
        />
        <Route
          path="/users"
          element={
            <ProtectedRoute>
              <Users />
            </ProtectedRoute>
          }
        />
        <Route
          path="/users/:id"
          element={
            <ProtectedRoute>
              <UserView />
            </ProtectedRoute>
          }
        />
        <Route
          path="/organizations"
          element={
            <ProtectedRoute>
              <Organizations />
            </ProtectedRoute>
          }
        />
        <Route
          path="/organizations/:userId"
          element={
            <ProtectedRoute>
              <OrganizationProfileDetail />
            </ProtectedRoute>
          }
        />
        <Route
          path="/systems"
          element={
            <ProtectedRoute>
              <Systems />
            </ProtectedRoute>
          }
        />
        <Route
          path="/sms-gateway"
          element={
            <ProtectedRoute>
              <SmsGateway />
            </ProtectedRoute>
          }
        />
        <Route
          path="/systems/new"
          element={
            <ProtectedRoute>
              <SystemRegistry />
            </ProtectedRoute>
          }
        />
        <Route
          path="/desk/:productKey"
          element={
            <ProtectedRoute>
              <ProductDesk />
            </ProtectedRoute>
          }
        />
        <Route
          path="/support"
          element={
            <ProtectedRoute>
              <SupportInbox />
            </ProtectedRoute>
          }
        />
        <Route
          path="/support/:caseId"
          element={
            <ProtectedRoute>
              <SupportCaseReview />
            </ProtectedRoute>
          }
        />
        <Route
          path="/safety"
          element={
            <ProtectedRoute>
              <SafetyDesk />
            </ProtectedRoute>
          }
        />
        <Route
          path="/verification"
          element={
            <ProtectedRoute>
              <VerificationDesk />
            </ProtectedRoute>
          }
        />
        <Route
          path="/security-ops"
          element={
            <ProtectedRoute>
              <SecurityOperations />
            </ProtectedRoute>
          }
        />
        <Route
          path="/platform-health"
          element={
            <ProtectedRoute>
              <PlatformHealth />
            </ProtectedRoute>
          }
        />
        <Route
          path="/privacy-ops"
          element={
            <ProtectedRoute>
              <PrivacyOps />
            </ProtectedRoute>
          }
        />
        <Route
          path="/monetization"
          element={
            <ProtectedRoute>
              <MonetizationReadiness />
            </ProtectedRoute>
          }
        />
        <Route
          path="/content"
          element={
            <ProtectedRoute>
              <ContentControl />
            </ProtectedRoute>
          }
        />
        <Route
          path="/notifications"
          element={
            <ProtectedRoute>
              <Notifications />
            </ProtectedRoute>
          }
        />

        {legacyRouteTargets.map(([path, to]) => (
          <Route key={path} path={path} element={<LegacyRedirect to={to} />} />
        ))}
        <Route path="/product-desks/:productKey" element={<LegacyDeskRedirect />} />

        <Route path="*" element={<SessionAwareFallback />} />
      </Routes>
    </Router>
  );
}
